import './location-section.css';

import EventEmitter from 'eventemitter3';
import { INewName } from '../../../../../interfaces/global.interfaces';
import { OrderType, Pages } from '../../../../../types/enums';
import { Data } from '../../../../../models/data';
import { Order } from '../../../../../models/order';
import { EventTypes } from '../../../../../core/event-bus';
import { Util } from '../../../../../utils/util';
import { ILocationProps } from '../../../Locations/locations.interface';
import { IFormattedAddress } from '../../../../../utils/util.interface';
import { ILocationSection } from './location-section.interface';
import { TwoWayBindingHelper } from '../../../../../utils/two-way-binding';
import { Context } from '../../../../../models/context';
import { LocalizationService } from '../../../../../services/localization-service';

/**
 * Manages the location section in the checkout process, allowing users to update their location
 * and toggle contactless delivery options.
 */
export class LocationSection {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;
    private readonly _bindingHelper: TwoWayBindingHelper<ILocationSection>;
    private _state: ILocationSection;
    private _containerElement: HTMLElement;

    /**
     * Initializes the LocationSection component.
     * @param {EventEmitter} eventBus - The event bus for emitting and listening to events.
     * @param {Data} data - The data object containing store and brand information.
     * @param {Order} order - The order instance for retrieving order-related data.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._order = order;
        this._state = this._createInitialState();
        this._bindingHelper = new TwoWayBindingHelper(this._state);

        this._containerElement = document.createElement("div");
        this._containerElement.classList.add("checkout-location");

        this.render();
        this._setTwoWayListeners();
    }

    /**
     * Initializes the state for the location section.
     * @returns {ILocationSection} The initial state object.
     * @private
     */
    private _createInitialState(): ILocationSection {
        return {
            contactless: false
        };
    }

    /**
     * Sets up two-way data binding for the contactless checkbox.
     * @private
     */
    private _setTwoWayListeners(): void {
        this._bindingHelper.bindToInput("contactless", "chk_checkout_contactless", this._containerElement);
    }

    /**
     * Updates the component state and re-renders necessary UI elements.
     * @param {Partial<ILocationSection>} newState - The updated state values.
     * @private
     */
    private _setState(newState: Partial<ILocationSection>): void {
        this._state = { ...this._state, ...newState };
        this._setTwoWayListeners();
        this.render();
    }

    /**
     * Handles updating the user's location based on the order type.
     * Triggers a location change event and sets relevant options.
     * @private
     */
    private readonly _handleUpdateLocation = (): void => {
        const orderTypeId = this._order.getProperty("OrderTypeID");

        const options: ILocationProps = {
            forOrderType: orderTypeId,
            onContinue: this._handleLocationContinue,
            isSave: true
        };

        // Adjust options for delivery type
        if (orderTypeId === OrderType.DELIVERY) {
            options.forOrderType = OrderType.DELIVERY;
            options.hideMap = true;
        }

        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, options);
    }

    /**
     * Determines the next navigation step after updating the location.
     * Redirects to either the menu or checkout based on the cart contents.
     * @private
     */
    private readonly _handleLocationContinue = (): void => {
        const hasItemsInCart = this._order.getProperty("Items").length > 0;
        const nextPage = hasItemsInCart ? Pages.CHECKOUT : Pages.MENU;

        this._eventBus.emit(EventTypes.LOCATION_CHANGE, nextPage);
    }

    private readonly _handleContactlessToggle = (): void => {
        if (this._order.getProperty("OrderTypeID") !== OrderType.DELIVERY)
            this._setState({ contactless: false });

        this._eventBus.emit(EventTypes.CHECKOUT_CONTACTLESS);
    }

    /**
     * Renders the location section UI.
     */
    private render(): void {
        // Check if the main container exists, create if not
        if (!this._containerElement.innerHTML) {
            this._containerElement.innerHTML = `
                <div class="checkout-location">
                    <div class="checkout-header checkout-header-extra">
                        <h2 id="p_checkout_location_header"></h2>
                        ${this._context.getProperty("isOrderHub") ? "" :
                            `<button id="btn_checkout_location_update" class="update-btn button-link" ltag="Update">${this._localizationService.names("Update")}</button>`
                        }
                    </div>

                    <address id="div_checkout_location_address"></address>

                    <div id="div_checkout_contactless" class="checkout-contactless hidden">
                        <div class="checkRadio">
                            <input type="checkbox" id="chk_checkout_contactless" ${this._state.contactless ? "checked" : ""}>
                            <label for="chk_checkout_contactless" ltag="Contactless">${this._localizationService.names("Contactless")}</label>
                        </div>
                        <p id="p_checkout_contactless_info" ltag="ContactlessInfo">${this._localizationService.names("ContactlessInfo")}</p>
                    </div>
                </div>`;

            this._containerElement.querySelector("#btn_checkout_location_update")?.addEventListener("click", this._handleUpdateLocation);
            this._containerElement.querySelector("#chk_checkout_contactless")?.addEventListener("change", this._handleContactlessToggle);
        }

        // Update only dynamic content
        this._containerElement.querySelector("#p_checkout_location_header")!.innerHTML = this._getLocationHeader();
        this._containerElement.querySelector("#div_checkout_location_address")!.innerHTML = this._getAddressHtml();

        // Show/hide contactless section dynamically
        this._renderContactless();
    }

    /**
     * Retrieves the location header text based on the current order type.
     * @returns {string} The formatted location header text.
     * @private
     */
    private _getLocationHeader(): string {
        const orderTypeSubType = this._data.getProperty("OrderTypeSubTypes").find(x => x.OrderTypeId == this._order.getProperty("OrderTypeID"));
        if (!orderTypeSubType)
            return "";

        const orderTypeName = this._localizationService.GetName(orderTypeSubType.Names);
        const labelKey = this._order.getProperty("OrderTypeID") === OrderType.DELIVERY ? "DeliveryTo" : "CarryoutLocation";

        const name = this._localizationService.names(labelKey).replace("{{order_type_name}}", orderTypeName);
        const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
            const tempText = this._localizationService.names(labelKey, lang).replace("{{order_type_name}}", this._localizationService.GetName(orderTypeSubType.Names, lang));
            return { lc: lang, text: tempText };
        });

        return `<span ltagj="${Util.toLtagj(nameObj)}">${name}</span>`;
    }

    /**
     * Retrieves the formatted address HTML.
     * @returns {string} The formatted address HTML string.
     * @private
     */
    private _getAddressHtml(): string {
        const address = this._getAddress();
        const apt = address?.address2 ? `${address.address2}` : "";
        return address ? `${address.address1}${apt},<br>${address.cityState}` : "";
    }

    /**
     * Retrieves the order's address information.
     * @returns {IFormattedAddress | null} The formatted address object, or null if unavailable.
     * @private
     */
    private _getAddress(): IFormattedAddress | null {
        if (this._order.getProperty("OrderTypeID") === OrderType.DELIVERY) {
            let deliveryAddress = this._order.getProperty("Address");
            if (deliveryAddress === null) {
                deliveryAddress = this._order.getLastDeliveryAddress();
                this._order.setAddress(deliveryAddress);
                if (deliveryAddress) {
                    const deliveryAddressStoreKey = this._order.getLastDeliveryAddressStore();
                    if (deliveryAddressStoreKey)
                        this._eventBus.emit(EventTypes.STORE_CHANGED, deliveryAddressStoreKey);
                }
            }

            if (deliveryAddress) {
                return Util.formatAddressObject({
                    StreetNo: deliveryAddress.StreetNo,
                    Address1: deliveryAddress.StreetName,
                    Address2: deliveryAddress.Address2,
                    Address3: deliveryAddress.Address3,
                    Address4: deliveryAddress.Address4,
                    Address5: deliveryAddress.Address5,
                    City: deliveryAddress.City,
                    State: deliveryAddress.State,
                    ZipCode: deliveryAddress.Zip,
                    CountryID: deliveryAddress.CountryID || 1,
                    AddressTypeID: deliveryAddress.AddressTypeID
                }, this._data.getProperty("Countries") || []);
            }
        } else {
            const store = this._data.getProperty("StoreInfo");
            return Util.formatAddressObject({
                StreetNo: store.streetNumber,
                Address1: store.address1,
                Address2: store.address2,
                Address3: store.address3,
                Address4: store.address4,
                Address5: store.address5,
                City: store.city,
                State: store.state,
                ZipCode: store.zip,
                CountryID: store.countryId || 1,
                AddressTypeID: 0
            }, this._data.getProperty("Countries") || []);
        }

        return null;
    }

    /**
     * Updates the visibility of the contactless delivery option based on settings.
     * @private
     */
    private _renderContactless(): void {
        const contactless = this._containerElement.querySelector("#div_checkout_contactless") as HTMLElement;
        if (!contactless)
            return;

        this._shouldShowContactless() ? Util.showElement(contactless) : Util.hideElement(contactless);
    }

    /**
     * Determines whether the contactless option should be visible.
     * @returns {boolean} True if contactless should be shown, false otherwise.
     * @private
     */
    private _shouldShowContactless(): boolean {
        return this._data.getProperty("Settings").CLESS === 1 && this._order.getProperty("OrderTypeID") === OrderType.DELIVERY;
    }

    /**
     * Retrieves the root element of the location section.
     * @returns {HTMLElement} The container element.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Gets the current contactless state.
     * @returns {boolean} Whether contactless delivery is enabled.
     */
    public get contactless(): boolean {
        return this._state.contactless;
    }

    /**
     * Sets the contactless delivery state and updates the UI accordingly.
     * @param {boolean} contactless - The new state of contactless delivery.
     */
    public set contactless(contactless: boolean) {
        this._setState({ contactless: contactless });
    }

    /**
     * Initializes the component and renders it.
     */
    public init(): void {
        this.render();
    }
}